import {Inject, Injectable} from '@angular/core';
import {LibConfig, LibConfigService} from '../lib.config';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {BaseObservableService} from './base-obeservable-service';
import {Timecard} from '../_models/timecard';
import {BehaviorSubject, Observable} from 'rxjs';
import moment from 'moment';
import {TimecardTypes} from '../_constants/timecard-types';
import {Constants} from '../_constants/constants';
import {User} from '../_models/user';
import {PinLoginComponent} from '../_components/pin-login/pin-login.component';
import {FeatureKeys} from '../_constants/feature-keys';
import {ClockInTypeComponent} from '../_components/clock-in-type/clock-in-type.component';
import {formatDate} from '@angular/common';
import {addCurrency} from '../_utils/currency-math';
import {ToastService} from './toast.service';
import {PrinterService} from './printer.service';
import {Organization} from '../_models/organization';
import {PickupLocation} from '../_models/pickup-location';
import {UserService} from './user.service';
import {OrganizationService} from './organization.service';
import {PickupLocationService} from './pickup-location.service';
import {ModalController} from '@ionic/angular';
import {SelectPickupLocationComponent} from '../_components/select-pickup-location/select-pickup-location.component';

@Injectable({
    providedIn: 'root'
})
export class TimecardService extends BaseObservableService<Timecard> {
    destination = 'timecards';

    public timecardSearchCriteria: Observable<any>;
    private timecardSearchCriteriaSubject: BehaviorSubject<any>;

    currentUser: User;
    organization: Organization;
    pickupLocation: PickupLocation;

    constructor(
        @Inject(LibConfigService) config: LibConfig,
        http: HttpClient,
        private userService: UserService,
        private printerService: PrinterService,
        private organizationService: OrganizationService,
        private pickupLocationService: PickupLocationService,
        private modalController: ModalController,
        private toastService: ToastService
    ) {
        super(http, config);

        const dateSetter = moment();
        dateSetter.set({hour: 0, minute: 0, second: 0, millisecond: 0});
        dateSetter.subtract(14, 'days');
        const startDate = dateSetter.format();
        dateSetter.add(15, 'days');
        const endDate = dateSetter.format();

        this.timecardSearchCriteriaSubject = new BehaviorSubject<any>({
            cacheTime: new Date(),
            startDate,
            endDate,
            searchString: '',
            pickupLocationIds: [],
            tipped: TimecardTypes.ALL.value
        });
        this.timecardSearchCriteria = this.timecardSearchCriteriaSubject.asObservable();

        this.organizationService.current.subscribe(o => this.organization = !!o ? new Organization(o) : null);
        this.userService.current.subscribe(u => this.currentUser = u);
        this.pickupLocationService.current.subscribe(p => this.pickupLocation = !!p ? new PickupLocation(p) : null);
    }

    clockIn(timecard: Timecard) {
        const headers = new HttpHeaders().set(Constants.USER_ORGANIZATION_TOKEN, 'true');
        return this.http.post(`${this.baseUrl()}/clock-in`, timecard, {headers});
    }

    update(timecard: Timecard) {
        return this.http.put(`${this.baseUrl()}/`, timecard);
    }

    clockOut(timecardId: number) {
        return this.http.put(`${this.baseUrl()}/${timecardId}/clock-out`, {});
    }

    clockOutByUser(user: User) {
        let headers = new HttpHeaders().set(Constants.USER_ORGANIZATION_TOKEN, 'true');
        return this.http.put(`${this.baseUrl()}/clock-out`, user, {headers});
    }

    active(organizationId: number, pickupLocations = null) {
        let url = `${this.baseUrl()}/active/${organizationId}`;

        if (!!pickupLocations && pickupLocations.length > 0) {
            url += `?pickupLocationIds=${pickupLocations.join(',')}`;
        }

        return this.http.get(url);
    }

    flagged(organizationId: number, pickupLocations = null) {
        let url = `${this.baseUrl()}/flagged/${organizationId}`;

        if (!!pickupLocations && pickupLocations.length > 0) {
            url += `?pickupLocationIds=${pickupLocations.join(',')}`;
        }

        return this.http.get(url);
    }

    activeUserTimecard(organizationId: number, silent = false) {
        const headers = silent ? new HttpHeaders().set(Constants.SILENT, 'true') : null;
        return this.http.get(`${this.baseUrl()}/active/${organizationId}/user`, {headers});
    }

    getDateRangeTimecardsSearch(
        orgId: number,
        pickupLocations: number[],
        personId: number,
        startDate: string,
        endDate: string,
        tipped: string,
        search: string
    ) {
        let url = `${this.baseUrl()}/date-range/${orgId}?`;
        url += `startDate=${startDate.replace('+', '%2b')}`;
        if (!!endDate) {
            url += `&endDate=${endDate.replace('+', '%2b')}`;
        }
        if (!!personId) {
            url += `&personId=${personId}`;
        }
        if (!!pickupLocations && pickupLocations.length > 0) {
            url += `&pickupLocationIds=${pickupLocations.join(',')}`;
        }
        if (!!search) {
            url += `&search=${search}`;
        }
        if (!!tipped) {
            const tippedVal = this.getTippedVal(tipped);
            url += `${(tippedVal != null) ? '&tipped=' + tippedVal : ''}`;
        }
        return this.http.get(url);
    }

    getUserDateRangeTimecardsSearch(
        orgId: number,
        startDate: string,
        endDate: string
    ) {
        let url = `${this.baseUrl()}/user/date-range/${orgId}?`;
        url += `startDate=${startDate}`;
        if (!!endDate) {
            url += `&endDate=${endDate}`;
        }
        return this.http.get(url);
    }

    getSummaryDateRangeTimecardsSearch(
        orgId: number,
        pickupLocations: number[],
        startDate: string,
        endDate: string,
        tipped: string,
        search: string
    ) {
        let url = `${this.baseUrl()}/summary/${orgId}?`;
        url += `${(!!pickupLocations && pickupLocations.length > 0) ? '&pickupLocationIds=' + pickupLocations.join(',') : ''}`;
        url += `&startDate=${startDate.replace('+', '%2b')}`;
        url += `&endDate=${endDate.replace('+', '%2b')}`;
        url += `&search=${search}`;

        const tippedVal = this.getTippedVal(tipped);
        url += `${(tippedVal != null) ? '&tipped=' + tippedVal : ''}`;

        return this.http.get(url);
    }

    rawData(
        orgId: number,
        pickupLocations: number[],
        startDate: string,
        endDate: string,
        tipped: string,
        search: string
    ) {
        const httpOptions = {
            responseType: 'blob' as 'json',
        };

        let url = `${this.baseUrl()}/export-raw/${orgId}?`;
        url += `${(!!pickupLocations && pickupLocations.length > 0) ? '&pickupLocationIds=' + pickupLocations.join(',') : ''}`;
        url += `&startDate=${startDate.replace('+', '%2b')}`;
        url += `&endDate=${endDate.replace('+', '%2b')}`;
        url += `&search=${search}`;

        const tippedVal = this.getTippedVal(tipped);
        url += `${(tippedVal != null) ? '&tipped=' + tippedVal : ''}`;

        return this.http.get(url, httpOptions);
    }

    getTippedVal(tipped) {
        if (tipped === 'true') {
            return true;
        } else if (tipped === 'false') {
            return false;
        }
        return null;
    }

    setTimecardSearchCriteria(value: any) {
        value.cacheTime = new Date();
        this.timecardSearchCriteriaSubject.next(value);
    }

    getTimecardSearchCriteria(): any {
        const val = this.timecardSearchCriteriaSubject.getValue();
        const current = new Date();
        const difference = current.getTime() - val.cacheTime.getTime();
        const hoursMilli = 1000 * 60 * 60; // 1 hour
        if (Math.abs(difference) < hoursMilli) {
            val.cacheTime = new Date();
            this.timecardSearchCriteriaSubject.next(val);
            return val;
        } else {
            const dateSetter = moment();
            dateSetter.set({hour: 0, minute: 0, second: 0, millisecond: 0});
            dateSetter.add(1, 'days');

            const endDate = dateSetter.format();
            dateSetter.subtract(8, 'days');
            const startDate = dateSetter.format();
            const newVal = {
                cacheTime: new Date(),
                startDate,
                endDate,
                searchString: '',
                pickupLocationIds: [],
                tipped: TimecardTypes.ALL.value
            };
            this.timecardSearchCriteriaSubject.next(newVal);
            return newVal;
        }
    }

    async pin(message: string): Promise<User> {
        return new Promise(async (resolve) => {
            const modal = await this.modalController.create({
                component: PinLoginComponent,
                componentProps: {
                    terminal: true,
                    fullLogin: false,
                    message
                },
                backdropDismiss: false,
                keyboardClose: false,
                cssClass: 'full-screen-modal'
            });
            await modal.present();

            modal.onDidDismiss().then(async (returnData) => {
                if (!!returnData && (!!returnData.data || returnData.data === 0)) {
                    resolve(new User(returnData.data));
                } else {
                    resolve(null);
                }
            });
        });
    }

    async presentClockIn() {
        const pickupLocation = !!this.pickupLocation ? this.pickupLocation
            : (!!this.organization && !!this.organization.pickupLocations && this.organization.pickupLocations.length === 1
                ? this.organization.pickupLocations[0]
                : null);
        if (!!pickupLocation) {
            const user: User = await this.pin('Clock In');
            if (!!user) {
                const timecard = new Timecard();
                timecard.person = user.person;
                timecard.tipped = false;
                timecard.pickupLocationId = pickupLocation.id;
                timecard.parentId = this.organization.id;

                if (user.authorizedTo(FeatureKeys.TIMECARD_TIPPED, this.organization.id)) {
                    const modal = await this.modalController.create({
                        component: ClockInTypeComponent,
                        componentProps: {
                            user
                        },
                        cssClass: 'select-button-modal'
                    });

                    modal.onDidDismiss().then(dataReturned => {
                        if (!!dataReturned && !!dataReturned.data) {
                            timecard.tipped = dataReturned.data === 'TIPPED';
                            this.clockIn(timecard).subscribe((t: Timecard) => {
                                this.setEvent(new Timecard(t));
                                user.timecard = t;
                                const clockedInTime = formatDate(t.clockInTime, 'MM/dd/YYYY hh:mm aa', 'en-us', null);
                                this.toastService.success(`${user.person.name} clocked in at ${clockedInTime}`);
                                if (!!this.currentUser && user.id === this.currentUser.id) {
                                    this.userService.setCurrent(user);
                                }

                                if (this.printerService.isConnected()) {
                                    this.printerService.printTimeCard(new Timecard(t), this.organization);
                                }
                            });
                        }
                    });

                    await modal.present();
                } else {
                    this.clockIn(timecard).subscribe((t: Timecard) => {
                        user.timecard = t;
                        this.toastService.success(`${user.person.name} clocked in.`);
                        if (!!this.currentUser && user.id === this.currentUser.id) {
                            this.userService.setCurrent(user);
                        }

                        if (this.printerService.isConnected()) {
                            this.printerService.printTimeCard(new Timecard(t), this.organization);
                        }
                    });
                }
            }
        } else {
            const modal = await this.modalController.create({
                component: SelectPickupLocationComponent,
                componentProps: {
                    pickupLocations: this.organization.pickupLocations,
                    selected: this.pickupLocation
                }
            });

            modal.onDidDismiss().then((dataReturned) => {
                if (dataReturned !== null && dataReturned.data != null) {
                    this.pickupLocation = new PickupLocation(dataReturned.data);
                    this.pickupLocationService.setCurrent(this.pickupLocation);
                    this.presentClockIn();
                }
            });

            await modal.present();
        }
    }

    async presentClockOut() {
        const user: User = await this.pin('Clock Out');
        if (!!user) {
            this.clockOutByUser(user).subscribe((t: Timecard) => {
                if (!!t) {
                    this.toastService.success(`${user.person.name} clocked out.`);
                    if (!!this.currentUser && user.id === this.currentUser.id) {
                        this.currentUser.timecard = null;
                        this.userService.setCurrent(this.currentUser);
                    }

                    if (this.printerService.isConnected()) {
                        this.organizationService.getEmployeeTipReport(this.organization.id, t.clockInTime, t.clockOutTime,
                            t.person.id)
                            .subscribe((employeeTipSummaries: any[]) => {
                                const tc = new Timecard(t);
                                tc.cashTips = 0;
                                tc.payrollTips = 0;
                                if (!!employeeTipSummaries) {
                                    employeeTipSummaries.forEach(ts => {
                                        tc.cashTips = addCurrency(tc.cashTips, ts.cashTips);
                                        tc.payrollTips = addCurrency(tc.payrollTips, ts.payrollTips);
                                    });
                                }
                                this.printerService.printTimeCard(tc, this.organization);
                            });
                    }
                } else {
                    this.toastService.error(`${user.person.name} is not clocked in.`);
                }
            });
        }
    }
}
